/**
 * LLMAgentInsights
 *
 * Displays LLM agent reasoning and insights during wizard steps.
 * Shows the AI's analysis, decisions, and context.
 * Now includes step progress with interesting facts during long-running operations.
 */

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { Sparkles, Brain, DollarSign, Lightbulb, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface StepProgress {
  currentItem?: number;
  totalItems?: number;
  currentCategory?: string;
  message?: string;
  percentComplete?: number;
  interestingFact?: string;
}

interface LLMAgentInsightsProps {
  contextSummary?: string;
  sampledProducts?: Array<{
    category: string;
    productCount: number;
    samples: Array<{ name: string; brand?: string }>;
  }>;
  detectedSector?: {
    id: string;
    name: string;
    description?: string;
  };
  totalLlmCostUsd?: number;
  stepProgress?: StepProgress;
  className?: string;
}

export function LLMAgentInsights({
  contextSummary,
  sampledProducts,
  detectedSector,
  totalLlmCostUsd,
  stepProgress,
  className
}: LLMAgentInsightsProps) {
  const hasContent = contextSummary || sampledProducts?.length || detectedSector;
  const isProcessing = stepProgress && stepProgress.currentItem !== undefined;

  return (
    <Card className={cn('bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-950/20 dark:to-blue-950/20 border-purple-200 dark:border-purple-800', className)}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm">
          <Brain className="h-4 w-4 text-purple-600" />
          AI Agent Insights
          {totalLlmCostUsd !== undefined && totalLlmCostUsd > 0 && (
            <Badge variant="outline" className="ml-auto text-xs">
              <DollarSign className="h-3 w-3 mr-1" />
              {Number(totalLlmCostUsd).toFixed(4)}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-48">
          {/* Step Progress Display */}
          {isProcessing && (
            <div className="space-y-3 mb-4">
              <div className="flex items-center gap-2 text-sm">
                <Loader2 className="h-4 w-4 animate-spin text-purple-600" />
                <span className="font-medium text-purple-700 dark:text-purple-400">
                  {stepProgress.message || 'Processing...'}
                </span>
              </div>

              {stepProgress.totalItems && (
                <div className="space-y-1">
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Progress: {stepProgress.currentItem} of {stepProgress.totalItems}</span>
                    <span>{stepProgress.percentComplete || 0}%</span>
                  </div>
                  <Progress value={stepProgress.percentComplete || 0} className="h-2" />
                </div>
              )}

              {stepProgress.currentCategory && (
                <div className="text-xs text-muted-foreground">
                  Currently analyzing: <span className="font-medium">{stepProgress.currentCategory}</span>
                </div>
              )}

              {stepProgress.interestingFact && (
                <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
                  <div className="flex items-start gap-2">
                    <Lightbulb className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="text-xs">
                      <span className="font-medium text-amber-700 dark:text-amber-400">Did you know? </span>
                      <span className="text-amber-900 dark:text-amber-200">{stepProgress.interestingFact}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {!hasContent && !isProcessing ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Sparkles className="h-4 w-4" />
              Waiting for data to analyze...
            </div>
          ) : (
            <div className="space-y-4 text-sm">
              {/* Context Summary */}
              {contextSummary && (
                <div className="space-y-1">
                  <h4 className="font-medium text-purple-700 dark:text-purple-400">
                    Business Understanding
                  </h4>
                  <p className="text-muted-foreground">{contextSummary}</p>
                </div>
              )}

              {/* Detected Sector */}
              {detectedSector && (
                <div className="space-y-1">
                  <h4 className="font-medium text-purple-700 dark:text-purple-400">
                    Detected Sector
                  </h4>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">{detectedSector.name}</Badge>
                    {detectedSector.description && (
                      <span className="text-xs text-muted-foreground">
                        {detectedSector.description}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Sampled Products */}
              {sampledProducts && sampledProducts.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium text-purple-700 dark:text-purple-400">
                    Product Samples Used for Context
                  </h4>
                  <div className="space-y-1">
                    {sampledProducts.slice(0, 5).map((cat, idx) => (
                      <div key={idx} className="text-xs">
                        <span className="font-medium">{cat.category}</span>
                        <span className="text-muted-foreground">
                          {' '}({cat.productCount} products)
                        </span>
                        <div className="text-muted-foreground pl-2">
                          {cat.samples.slice(0, 2).map(p => p.name).join(', ')}
                          {cat.samples.length > 2 && `, +${cat.samples.length - 2} more`}
                        </div>
                      </div>
                    ))}
                    {sampledProducts.length > 5 && (
                      <div className="text-xs text-muted-foreground">
                        +{sampledProducts.length - 5} more categories
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
